import { Loader } from "@googlemaps/js-api-loader"
import { ref } from "vue"
import { Location } from "@/models"

export function useGooglePlaceSearch(apiKey: string) {
  const autocomplete = ref<google.maps.places.Autocomplete | null>(null)

  const load = async (inputRef: HTMLInputElement) => {
    const loader = new Loader({
      apiKey,
      libraries: ["places"],
    })

    const { Autocomplete } = await loader.importLibrary("places")

    const options = {
      fields: [
        "address_components",
        "geometry",
        "place_id",
        "name",
        "formatted_address",
        "types",
      ],
    }

    autocomplete.value = new Autocomplete(inputRef, options)

    return autocomplete.value
  }

  const extractPlaceDetails = (place: google.maps.places.PlaceResult): Location => {
    const googleId = place?.place_id
    const placeName = place?.name
    const latitude = place?.geometry?.location?.lat()
    const longitude = place?.geometry?.location?.lng()
    const formattedAddress = place?.formatted_address
    const website = place?.website

    const getAddressComponent = (type: string) => {
      const component = place.address_components?.find(component => component.types?.includes(type))
      return component?.long_name ?? component?.short_name
    }

    const pointOfInterest = place.types?.includes("point_of_interest")
    const establishment = place.types?.includes("establishment")

    return new Location({
      googleId,
      website,
      name: placeName,
      latitude,
      longitude,
      formattedAddress,
      streetNumber: getAddressComponent("street_number"),
      streetAddress: getAddressComponent("route"),
      city: getAddressComponent("locality"),
      state: getAddressComponent("administrative_area_level_1"),
      region: getAddressComponent("administrative_area_level_2"),
      zipCode: getAddressComponent("postal_code"),
      country: getAddressComponent("country"),
      pointOfInterest,
      establishment,
    })
  }

  return { autocomplete, load, extractPlaceDetails }
}
